import {
  Avatar,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  SxProps,
  Typography,
} from '@mui/material';
import {FC, useContext, useState} from 'react';
import {useParams} from 'react-router-dom';
import {useRecoilValueLoadable, useSetRecoilState} from 'recoil';
import {ServiceRequestService} from '../../../../api/services/serviceRequest.service';
import {LoadableHelpers} from '../../../../shared/helpers/loadableHelpers';
import {IUserAccount} from '../../../../shared/types/user-account.types';
import {serviceRequestsQueryTrigger} from '../../../../store/service-requests/atoms';
import {managerListItemSelector} from '../store/manager-list-item.store';
import {managerListModalAtom} from '../store/manager-list-modal.store';
import {EventBusContext} from '../../../../providers/EventBus';

interface Props {
  user: IUserAccount;
}

const STYLES = {
  container: {
    borderRadius: 2,
    overflow: 'hidden',
  },
  button: {
    height: 60,
  },
} as const satisfies Record<string, SxProps>;

export const ManagerListItem: FC<Props> = ({user}) => {
  const {id} = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const triggerQuery = useSetRecoilState(serviceRequestsQueryTrigger);
  const setListModalVisible = useSetRecoilState(managerListModalAtom);
  const loadable = useRecoilValueLoadable(managerListItemSelector(user.id));
  const {bus} = useContext(EventBusContext);

  const handleOpenConfirm = () => {
    setIsConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setIsConfirmOpen(false);
  };

  const selectManager = async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      await ServiceRequestService.setManager(Number(id), user.uid);
      triggerQuery((p) => p + 1);
      setListModalVisible(false);
    } catch (e) {
      bus?.emit('error', [e]);
    } finally {
      setIsLoading(false);
      setIsConfirmOpen(false);
    }
  };

  return (
    <>
      <ListItem sx={STYLES.container} disablePadding>
        <ListItemButton sx={STYLES.button} onClick={handleOpenConfirm} disabled={isLoading}>
          <ListItemAvatar>
            <Avatar src={LoadableHelpers.withDefault(loadable, null) || ''} />
          </ListItemAvatar>
          <ListItemText primary={`${user.lastName} ${user.firstName}`} secondary={user.email} />
          {isLoading && <CircularProgress size={20} />}
        </ListItemButton>
      </ListItem>

      <Dialog open={isConfirmOpen} onClose={handleCloseConfirm}>
        <DialogTitle>Подтверждение смены менеджера</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите назначить менеджера{' '}
            {`${user.lastName} ${user.firstName}${user.patronymic ? ` ${user.patronymic}` : ''}`}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} disabled={isLoading}>
            Отмена
          </Button>
          <Button onClick={selectManager} variant="contained" color="primary" disabled={isLoading}>
            Ок
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
